import { CatalogItemCard } from '@/components/services/catalog-item-card';
import { ServiceCard } from '@/components/services/service-card';
import { EmptyState } from '@/components/ui/empty-state';
import { ErrorState } from '@/components/ui/error-state';
import { FadeInView } from '@/components/ui/fade-in-view';
import { FilterChips } from '@/components/ui/filter-chips';
import { PageLayout } from '@/components/ui/page-layout';
import { Pagination } from '@/components/ui/pagination';
import { SearchInput } from '@/components/ui/search-input';
import { SkeletonCard } from '@/components/ui/skeleton';
import { useDebounce } from '@/hooks/use-debounce';
import { useMembership } from '@/hooks/use-membership';
import { useServiceCatalog } from '@/hooks/use-service-catalog';
import { useProviderServices } from '@/hooks/use-services';
import { useTerms } from '@/hooks/use-terms';
import { router } from 'expo-router';
import React, { useState } from 'react';
import { StaticList } from '@/components/ui/static-list';
import { View } from 'react-native';

const DEFAULT_ITEMS_PER_PAGE = 10;

type SearchOption = 'all' | 'actives' | 'inactives';

/**
 * Em equipe existem duas listas da mesma coisa: o que o negócio vende (o
 * catálogo, um card por serviço) e o que EU faço (a minha cópia, com preço e
 * agenda). Solo só tem a segunda.
 */
type ScopeOption = 'mine' | 'team';

const scopeOptions = [
  { label: 'Meus', value: 'mine' as const },
  { label: 'Da equipe', value: 'team' as const },
];

const searchOptions = [
  { label: 'Todos', value: 'all' as const },
  { label: 'Ativos', value: 'actives' as const },
  { label: 'Inativos', value: 'inactives' as const },
];

export default function ProviderServicesScreen() {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchOption, setSearchOption] = useState<SearchOption>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_ITEMS_PER_PAGE);
  const [scope, setScope] = useState<ScopeOption>('mine');
  const debouncedSearchQuery = useDebounce(searchQuery, 500);
  const terms = useTerms();
  const { membership } = useMembership();
  const isTeam = membership?.type === 'TEAM';
  const { data: catalog = [], isLoading: catalogLoading } = useServiceCatalog({
    enabled: isTeam,
  });

  let isActiveFilter: boolean | undefined;
  if (searchOption === 'actives') {
    isActiveFilter = true;
  } else if (searchOption === 'inactives') {
    isActiveFilter = false;
  }

  const {
    data: paginatedData,
    isLoading,
    error,
    refetch,
  } = useProviderServices({
    q: debouncedSearchQuery || undefined,
    isActive: isActiveFilter,
    page: currentPage,
    limit: itemsPerPage,
  });

  const services = paginatedData?.data || [];
  // A busca do catálogo é local: a lista é curta e a API não pagina itens.
  const visibleCatalog = catalog.filter((item) =>
    debouncedSearchQuery
      ? item.name.toLowerCase().includes(debouncedSearchQuery.toLowerCase())
      : true,
  );
  const totalPages = paginatedData?.meta?.totalPages || 0;

  const handleNewService = () => {
    router.push('/(tabs)/(provider)/services/create');
  };

  const handleSearchOptionChange = (optionValue: SearchOption) => {
    setSearchOption(optionValue);
    setCurrentPage(1);
  };

  return (
    <PageLayout
      title={terms.title.servicePlural}
      subtitle="Tudo o que você oferece, num só lugar"
      buttonTitle={`Cadastrar ${terms.service}`}
      onButtonPress={handleNewService}
      buttonTestID="service-list-new-service-btn"
    >
      <View className="gap-2.5">
        {isTeam ? (
          <FilterChips
            options={scopeOptions}
            value={scope}
            onChange={(value: ScopeOption) => setScope(value)}
          />
        ) : null}
        <SearchInput
          placeholder={`Buscar ${terms.servicePlural}...`}
          value={searchQuery}
          onChangeText={setSearchQuery}
        />
        {scope === 'mine' ? (
          <FilterChips
            options={searchOptions}
            value={searchOption}
            onChange={handleSearchOptionChange}
          />
        ) : null}
      </View>

      {isTeam && scope === 'team' ? (
        catalogLoading ? (
          <View className="gap-3">
            {Array.from({ length: 4 }).map((_, index) => (
              <SkeletonCard key={index} showHeader={true} showFooter={false} lines={1} />
            ))}
          </View>
        ) : visibleCatalog.length === 0 ? (
          <EmptyState
            icon="pricetags-outline"
            title="A equipe ainda não vende nada"
            description={`Cadastre ${terms.agree('um', 'service')} ${terms.service}: ele entra no catálogo do negócio.`}
            actionLabel={`Criar ${terms.service}`}
            onAction={handleNewService}
            className="py-12"
          />
        ) : (
          <StaticList
            data={visibleCatalog}
            keyExtractor={(item) => item.id}
            renderItem={(item, index) => (
              <FadeInView key={item.id} delay={index * 50}>
                <CatalogItemCard item={item} />
              </FadeInView>
            )}
          />
        )
      ) : isLoading ? (
        <View className="gap-3">
          {Array.from({ length: 5 }).map((_, index) => (
            <SkeletonCard key={index} showHeader={true} showFooter={false} lines={2} />
          ))}
        </View>
      ) : error ? (
        <ErrorState
          variant="inline"
          title={`Erro ao carregar ${terms.servicePlural}`}
          message={`Não foi possível carregar ${terms.agree('os', 'service')} ${terms.servicePlural}. Tente novamente.`}
          onRetry={refetch}
        />
      ) : services.length === 0 ? (
        <EmptyState
          icon="briefcase-outline"
          title={`${terms.agree('Nenhum', 'service')} ${terms.service} ${terms.agree('encontrado', 'service')}`}
          description={
            debouncedSearchQuery || searchOption !== 'all'
              ? `Ajuste a busca ou crie ${terms.agree('um', 'service')} ${terms.agree('novo', 'service')} ${terms.service}.`
              : `Crie ${terms.agree('seu', 'service')} ${terms.agree('primeiro', 'service')} ${terms.service} para começar.`
          }
          actionLabel={`Criar ${terms.service}`}
          onAction={handleNewService}
          className="py-12"
        />
      ) : (
        <>
          <StaticList
            data={services}
            keyExtractor={(item) => item.id}
            renderItem={(item, index) => (
              <FadeInView key={item.id} delay={index * 50}>
                <ServiceCard service={item} size="large" />
              </FadeInView>
            )}
          />
          <View className="mt-2">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages || 1}
              onPageChange={setCurrentPage}
              itemsPerPage={itemsPerPage}
              onItemsPerPageChange={setItemsPerPage}
              itemsPerPageOptions={[10, 20, 50, 100]}
            />
          </View>
        </>
      )}
    </PageLayout>
  );
}
